import { useEffect, useState, type DragEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PIPELINE_STAGES, type PipelineCard } from '@tipstrack/shared';
import {
  fetchPipeline,
  listJobs,
  updateApplicationStage,
  type PipelineResponse,
} from '../api/endpoints.js';
import { errorMessage } from '../api/client.js';

interface DragPayload {
  applicationId: string;
  fromStage: string;
}

/** Move an application card between stage columns in a cached pipeline response. */
function moveInPipeline(
  data: PipelineResponse,
  applicationId: string,
  fromStage: string,
  toStage: string,
): PipelineResponse {
  let moved: PipelineCard | undefined;
  const stages = data.stages.map((col) => {
    if (col.key !== fromStage) return col;
    moved = col.applications.find((a) => a.id === applicationId);
    return { ...col, applications: col.applications.filter((a) => a.id !== applicationId) };
  });
  if (!moved) return data;
  const movedCard = moved;
  return {
    stages: stages.map((col) =>
      col.key === toStage ? { ...col, applications: [movedCard, ...col.applications] } : col,
    ),
  };
}

export function PipelinePage() {
  const queryClient = useQueryClient();
  const jobsQuery = useQuery({ queryKey: ['jobs'], queryFn: listJobs });
  const jobs = jobsQuery.data ?? [];

  const [jobId, setJobId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  // Default to the first open job (else the first job) once jobs load.
  useEffect(() => {
    if (!jobId && jobs.length > 0) {
      const firstOpen = jobs.find((j) => j.status === 'open');
      setJobId((firstOpen ?? jobs[0]).id);
    }
  }, [jobs, jobId]);

  const pipelineQuery = useQuery({
    queryKey: ['pipeline', jobId],
    queryFn: () => fetchPipeline(jobId),
    enabled: Boolean(jobId),
  });

  const stageMutation = useMutation({
    mutationFn: ({ applicationId, toStage }: DragPayload & { toStage: string }) =>
      updateApplicationStage(applicationId, toStage),
    // Optimistic move: update the cached board immediately, roll back on error.
    onMutate: async ({ applicationId, fromStage, toStage }) => {
      await queryClient.cancelQueries({ queryKey: ['pipeline', jobId] });
      const previous = queryClient.getQueryData<PipelineResponse>(['pipeline', jobId]);
      if (previous) {
        queryClient.setQueryData<PipelineResponse>(
          ['pipeline', jobId],
          moveInPipeline(previous, applicationId, fromStage, toStage),
        );
      }
      return { previous };
    },
    onError: (err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['pipeline', jobId], context.previous);
      }
      setError(errorMessage(err));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['pipeline', jobId] });
      void queryClient.invalidateQueries({ queryKey: ['pipeline-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  // fromStage is the column the card is rendered in — the card itself carries
  // no stage field.
  function onDragStart(e: DragEvent<HTMLDivElement>, card: PipelineCard, fromStage: string) {
    const payload: DragPayload = { applicationId: card.id, fromStage };
    e.dataTransfer.setData('application/json', JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
  }

  function onDrop(e: DragEvent<HTMLDivElement>, toStage: string) {
    e.preventDefault();
    setDragOverStage(null);
    let payload: DragPayload;
    try {
      payload = JSON.parse(e.dataTransfer.getData('application/json')) as DragPayload;
    } catch {
      return;
    }
    if (!payload?.applicationId || payload.fromStage === toStage) return;
    setError(null);
    stageMutation.mutate({ ...payload, toStage });
  }

  // Render columns in canonical order regardless of server ordering.
  const columnsByKey = new Map((pipelineQuery.data?.stages ?? []).map((s) => [s.key, s]));
  const columns = PIPELINE_STAGES.map((def) => ({
    ...def,
    applications: columnsByKey.get(def.key)?.applications ?? [],
  }));

  return (
    <div className="page page--wide">
      <header className="page__header">
        <h1>Pipeline</h1>
        <label className="field field--inline">
          <span className="field__label">Job</span>
          <select value={jobId} onChange={(e) => setJobId(e.target.value)}>
            {jobs.length === 0 && <option value="">No jobs</option>}
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.title} ({j.status})
              </option>
            ))}
          </select>
        </label>
      </header>

      {error && <div className="alert alert--error">{error}</div>}
      {pipelineQuery.isLoading && jobId && <div className="spinner" aria-label="Loading pipeline" />}
      {pipelineQuery.isError && (
        <div className="alert alert--error">Failed to load the pipeline board.</div>
      )}

      {!jobId && !jobsQuery.isLoading && (
        <div className="empty-state">
          <h2>No jobs yet</h2>
          <p>
            <Link to="/jobs">Create a job</Link> to start building a pipeline.
          </p>
        </div>
      )}

      {jobId && pipelineQuery.data && (
        <div className="kanban">
          {columns.map((col) => (
            <div
              key={col.key}
              className={`kanban__col${dragOverStage === col.key ? ' is-drag-over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDragEnter={() => setDragOverStage(col.key)}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverStage(null);
              }}
              onDrop={(e) => onDrop(e, col.key)}
            >
              <div className={`kanban__header stage-${col.key}`}>
                <span>{col.label}</span>
                <span className="kanban__count">{col.applications.length}</span>
              </div>
              <div className="kanban__cards">
                {col.applications.map((card) => (
                  <div
                    key={card.id}
                    className="kanban__card"
                    draggable
                    onDragStart={(e) => onDragStart(e, card, col.key)}
                  >
                    <Link to={`/applications/${card.id}`} className="kanban__card-name">
                      {card.candidateName}
                    </Link>
                    {card.candidateEmail && (
                      <div className="kanban__card-sub">{card.candidateEmail}</div>
                    )}
                    <div className="kanban__card-sub muted">
                      {card.isIndeed ? 'Indeed' : 'Manual'} ·{' '}
                      {new Date(card.appliedAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
                {col.applications.length === 0 && <div className="kanban__empty">Drop here</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
